import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import RichArticleEditor from '../components/RichArticleEditor';
import { useAuth } from '../hooks/useAuth';
import {
  listFarmers,
  listDoctors,
  listFarmerMonthlyReports,
  listPostsForAdmin,
  createFarmer,
  createDoctor,
  createMonthlyReport,
  createPost,
  createSoilTest,
  deleteDoctor,
  deleteFarmer,
  deleteMonthlyReport,
  deletePost,
  deleteSoilTest,
  cleanupExpiredPasswordViewLogs,
  getUserProfile,
  listPasswordViewLogs,
  listUserProfiles,
  logPasswordView,
  submitMonthlyReportForReview,
  submitPostForReview,
  updateDoctor,
  updateFarmer,
  updateMonthlyReport,
  updatePost,
  updateSoilTest,
} from '../lib/firestore';
import {
  convertBlocksToHtml,
  convertPlainTextToHtml,
  extractCloudinaryPublicIdsFromHtml,
  extractFirstImageFromHtml,
  extractImageUrlsFromHtml,
  extractPlainTextFromHtml,
  sanitizeArticleHtml,
} from '../lib/articleHtml';
import { decryptPasswordOnClient, PASSWORD_HINT } from '../lib/passwordCipher';
import { mergeDoctorProfiles } from '../data/doctors';
import { mergeFeaturedFarmers } from '../data/farmers';

const emptyFarmer = {
  name: '',
  slug: '',
  prefecture: '',
  city: '',
  farmerType: 'fulltime',
  crops: '',
  fieldAddress: '',
  environmentText: '',
  mapUrl: '',
  geoLat: '',
  geoLng: '',
  ownerUid: '',
  heroImageUrl: '',
  youtubeUrl: '',
};

const emptyDoctor = {
  name: '',
  title: '',
  clinic: '',
  specialty: '',
  detailPagePath: '',
  visibility: 'public',
  displayOrder: 0,
};

const emptyReport = {
  title: '',
  publishedDate: '',
  body: '',
  contentHtml: '<p></p>',
  introImageUrl: '',
  visibility: 'public',
  photos: [],
  tags: '',
};

const createEmptyPost = () => ({
  title: '',
  publishedDate: '',
  body: '',
  introImageUrl: '',
  youtubeUrl: '',
  tags: '',
  cropTags: '',
  contentHtml: '<p></p>',
  visibility: 'private',
  contentBlocks: [],
});

function slugifyTitle(value = '') {
  const normalized = String(value)
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || `post-${Date.now()}`;
}

function formatDoctorName(name = '') {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '';
  return trimmed.endsWith('先生') ? trimmed : `${trimmed} 先生`;
}

function getRemovedPostImagePublicIds(previousHtml = '', nextHtml = '') {
  const previousIds = extractCloudinaryPublicIdsFromHtml(previousHtml);
  const nextIds = new Set(extractCloudinaryPublicIdsFromHtml(nextHtml));
  return previousIds.filter((publicId) => !nextIds.has(publicId));
}

function normalizeTagList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

function normalizeFarmerPayload(farmer) {
  const { geoLat, geoLng, ...rest } = farmer;
  const lat = Number.parseFloat(geoLat);
  const lng = Number.parseFloat(geoLng);
  const hasGeo = Number.isFinite(lat) && Number.isFinite(lng);

  return {
    ...rest,
    crops: farmer.crops.split(',').map((v) => v.trim()).filter(Boolean),
    fieldAddress: (farmer.fieldAddress || '').trim(),
    environmentText: (farmer.environmentText || '').trim(),
    mapUrl: (farmer.mapUrl || '').trim(),
    geo: hasGeo ? { lat, lng } : null,
  };
}

function summarizeRecordChanges(previousRecord = {}, nextRecord = {}, fieldPairs = []) {
  const changes = [];

  for (const [label, previousValue, nextValue] of fieldPairs) {
    const before = Array.isArray(previousValue)
      ? previousValue.join(' / ')
      : String(previousValue || '').trim();
    const after = Array.isArray(nextValue)
      ? nextValue.join(' / ')
      : String(nextValue || '').trim();
    if (before === after) continue;
    changes.push(`${label}: ${before || '未設定'} -> ${after || '未設定'}`);
  }

  if (String(previousRecord.contentHtml || '').trim() !== String(nextRecord.contentHtml || '').trim()) {
    changes.push('本文を更新');
  }

  return changes;
}

function resolvePersistedIntroImageUrl(previousIntroImageUrl = '', previousImageUrls = [], nextImageUrls = []) {
  const trimmed = String(previousIntroImageUrl || '').trim();
  if (!nextImageUrls.length) return '';
  if (!trimmed) return nextImageUrls[0] || '';
  if (!trimmed.startsWith('blob:')) {
    return nextImageUrls.includes(trimmed) ? trimmed : (nextImageUrls[0] || '');
  }
  const previousIndex = previousImageUrls.findIndex((url) => url === trimmed);
  if (previousIndex >= 0 && nextImageUrls[previousIndex]) {
    return nextImageUrls[previousIndex];
  }
  return nextImageUrls[0] || '';
}

export default function AdminPage({ mode = 'all' }) {
  const {
    user,
    userProfile,
    operatorRole,
    isAdmin,
    isStaff,
    canManageOperatorPosts,
    canManageFarmerReports,
    roleLabel,
  } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('');

  const [farmer, setFarmer] = useState(emptyFarmer);
  const [targetFarmerId, setTargetFarmerId] = useState('');
  const [targetSoilId, setTargetSoilId] = useState('');
  const [farmerSubTab, setFarmerSubTab] = useState('create');
  const [farmerOptions, setFarmerOptions] = useState([]);
  const [farmerCreateStatus, setFarmerCreateStatus] = useState(null);

  const [soil, setSoil] = useState({ date: '', sampleLocation: '', labName: '', pH: '', EC: '', N: '', P: '', K: '', pdfEmbedUrl: '' });

  const [farmerPasswordUid, setFarmerPasswordUid] = useState('');
  const [farmerCipherKey, setFarmerCipherKey] = useState('');
  const [farmerPasswordCheckBusy, setFarmerPasswordCheckBusy] = useState(false);
  const [farmerPasswordCheckResult, setFarmerPasswordCheckResult] = useState(null);
  const [farmerPasswordCheckError, setFarmerPasswordCheckError] = useState('');
  const [farmerUserOptions, setFarmerUserOptions] = useState([]);
  const [farmerViewLogs, setFarmerViewLogs] = useState([]);

  const [doctorSubTab, setDoctorSubTab] = useState('create');
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [targetDoctorId, setTargetDoctorId] = useState('');
  const [doctor, setDoctor] = useState(emptyDoctor);
  const [doctorCreateStatus, setDoctorCreateStatus] = useState(null);

  const [report, setReport] = useState(emptyReport);
  const [initialReportContentHtml, setInitialReportContentHtml] = useState('<p></p>');
  const [reportEditorMode, setReportEditorMode] = useState('create');
  const [reportFarmerId, setReportFarmerId] = useState('');
  const [reportOptions, setReportOptions] = useState([]);
  const [targetReportId, setTargetReportId] = useState('');
  const [reportSubmitStatus, setReportSubmitStatus] = useState(null);
  const [reportSubmitDebug, setReportSubmitDebug] = useState(null);
  const [post, setPost] = useState(createEmptyPost);
  const [initialPostContentHtml, setInitialPostContentHtml] = useState('<p></p>');
  const [postOptions, setPostOptions] = useState([]);
  const [postFarmerCandidateId, setPostFarmerCandidateId] = useState('');
  const [postFarmerTagIds, setPostFarmerTagIds] = useState([]);
  const [postDoctorCandidateId, setPostDoctorCandidateId] = useState('');
  const [postDoctorTagIds, setPostDoctorTagIds] = useState([]);
  const [postFarmerOptions, setPostFarmerOptions] = useState([]);
  const [targetPostId, setTargetPostId] = useState('');
  const [postEditorTab, setPostEditorTab] = useState('farmer');
  const [postEditorMode, setPostEditorMode] = useState('create');
  const [postPreviewDevice, setPostPreviewDevice] = useState('');
  const [postSubmitStatus, setPostSubmitStatus] = useState(null);
  const [postSubmitDebug, setPostSubmitDebug] = useState(null);
  const [postPreviewChecks, setPostPreviewChecks] = useState({ pc: false, mobile: false });
  const reportEditorRef = useRef(null);
  const postEditorRef = useRef(null);

  const isAllMode = mode === 'all';
  const isFarmersMode = mode === 'farmers';
  const isDoctorsMode = mode === 'doctors';
  const isPostsMode = mode === 'posts';

  const pageTitle = isFarmersMode
    ? 'Drs.Farm | 農家関連'
    : isDoctorsMode
      ? 'Drs.Farm | Drs. Farmドクター関連'
      : isPostsMode
        ? 'Drs.Farm | 記事作成'
        : 'Drs.Farm | 管理';

  const dashboardRoleLabel = useMemo(() => roleLabel || (isAdmin ? 'サイト管理者' : isStaff ? '運営' : '未判定'), [isAdmin, isStaff, roleLabel]);
  const doctorTagOptions = useMemo(
    () => doctorOptions.filter((row) => row.visibility !== 'private'),
    [doctorOptions]
  );
  const isFarmerUser = userProfile?.role === 'farmer' && !isAdmin;
  const reportFarmerSourceOptions = useMemo(
    () => mergeFeaturedFarmers(farmerOptions),
    [farmerOptions]
  );
  const reportFarmerOptions = useMemo(() => {
    if (!isFarmerUser) return reportFarmerSourceOptions;
    const linkedFarmerId = userProfile?.linkedFarmerId || '';
    if (!linkedFarmerId) return [];
    return reportFarmerSourceOptions.filter((row) => row.id === linkedFarmerId);
  }, [isFarmerUser, reportFarmerSourceOptions, userProfile?.linkedFarmerId]);
  const doctorDetailPageOptions = useMemo(() => {
    const mergedRows = mergeDoctorProfiles(doctorOptions);
    const optionMap = new Map();

    for (const row of mergedRows) {
      const path = row.detailPagePath || ((row.slug || row.id) ? `/doctors/${row.slug || row.id}` : '');
      if (!path || optionMap.has(path)) continue;
      optionMap.set(path, {
        value: path,
        label: formatDoctorName(row.name || row.slug || row.id || path),
      });
    }

    return Array.from(optionMap.values());
  }, [doctorOptions]);
  const postContentImageOptions = useMemo(
    () => extractImageUrlsFromHtml(post.contentHtml || '<p></p>'),
    [post.contentHtml]
  );
  const reportContentImageOptions = useMemo(
    () => extractImageUrlsFromHtml(report.contentHtml || '<p></p>'),
    [report.contentHtml]
  );
  const reportIntroImageOptions = useMemo(() => {
    const options = [''];
    const currentIntro = String(report.introImageUrl || '').trim();
    if (currentIntro && !options.includes(currentIntro)) {
      options.push(currentIntro);
    }
    reportContentImageOptions.forEach((imageUrl) => {
      if (!options.includes(imageUrl)) options.push(imageUrl);
    });
    return options;
  }, [report.introImageUrl, reportContentImageOptions]);
  const postIntroImageOptions = useMemo(() => {
    const options = [''];
    const currentIntro = String(post.introImageUrl || '').trim();
    if (currentIntro && !options.includes(currentIntro)) {
      options.push(currentIntro);
    }
    postContentImageOptions.forEach((imageUrl) => {
      if (!options.includes(imageUrl)) options.push(imageUrl);
    });
    return options;
  }, [post.introImageUrl, postContentImageOptions]);
  const showSubmitDebug = useMemo(() => {
    if (searchParams.has('debug')) return true;
    if (typeof window === 'undefined') return false;
    return /[?&]debug=1(?:&|$)/.test(window.location.href);
  }, [searchParams]);
  const postEditorTabs = useMemo(() => {
    const tabs = [];
    if (canManageFarmerReports) tabs.push({ key: 'farmer', label: '農家近況報告' });
    if (canManageOperatorPosts) tabs.push({ key: 'operator', label: '運営記事' });
    return tabs;
  }, [canManageFarmerReports, canManageOperatorPosts]);

  function buildSubmitDebug(scope, mode, state, extra = {}) {
    return {
      scope,
      mode,
      state,
      at: new Date().toISOString(),
      uid: user?.uid || '',
      email: user?.email || '',
      operatorRole: operatorRole || '(none)',
      profileRole: userProfile?.role || '(none)',
      ...extra,
    };
  }

  useEffect(() => {
    async function loadFarmers() {
      const rows = await listFarmers();
      setFarmerOptions(rows);
    }
    loadFarmers();
  }, []);

  useEffect(() => {
    async function loadDoctors() {
      const rows = await listDoctors();
      setDoctorOptions(rows);
    }
    loadDoctors();
  }, []);

  useEffect(() => {
    async function loadPosts() {
      const rows = await listPostsForAdmin();
      setPostOptions(rows);
    }
    loadPosts();
  }, []);

  useEffect(() => {
    async function loadTagOptionsFromPublicSiteData() {
      const [farmersRows] = await Promise.all([
        listFarmers(),
      ]);
      const farmersPublic = mergeFeaturedFarmers(farmersRows).filter((row) => row.visibility !== 'private');
      setPostFarmerOptions(farmersPublic);
    }
    loadTagOptionsFromPublicSiteData();
  }, []);

  useEffect(() => {
    if (postEditorTab !== 'farmer') return;
    if (!reportFarmerOptions.length) {
      setReportFarmerId('');
      return;
    }
    if (!reportFarmerOptions.some((row) => row.id === reportFarmerId)) {
      setReportFarmerId(reportFarmerOptions[0].id);
      setTargetReportId('');
    }
  }, [postEditorTab, reportFarmerId, reportFarmerOptions]);

  useEffect(() => {
    async function loadFarmerPasswordContext() {
      if (!isFarmersMode || farmerSubTab !== 'password' || !isAdmin) return;
      const [users, logs] = await Promise.all([
        listUserProfiles({ role: 'farmer' }),
        listPasswordViewLogs(120),
      ]);
      setFarmerUserOptions(users);
      setFarmerViewLogs(logs.filter((row) => row.scope === 'admin_farmers'));
    }
    loadFarmerPasswordContext();
  }, [farmerSubTab, isAdmin, isFarmersMode]);

  useEffect(() => {
    if (!targetFarmerId || farmerSubTab !== 'info') return;
    const selected = farmerOptions.find((row) => row.id === targetFarmerId);
    if (!selected) return;
    setFarmer({
      name: selected.name || '',
      slug: selected.slug || '',
      prefecture: selected.prefecture || '',
      city: selected.city || '',
      farmerType: selected.farmerType || 'fulltime',
      crops: (selected.crops || []).join(','),
      fieldAddress: selected.fieldAddress || '',
      environmentText: selected.environmentText || '',
      mapUrl: selected.mapUrl || '',
      geoLat: selected.geo?.lat ?? '',
      geoLng: selected.geo?.lng ?? '',
      ownerUid: selected.ownerUid || '',
      heroImageUrl: selected.heroImageUrl || '',
      youtubeUrl: selected.youtubeUrl || '',
    });
  }, [targetFarmerId, farmerSubTab, farmerOptions]);

  useEffect(() => {
    if (!(isAllMode || isPostsMode)) return;

    const nextEditor = searchParams.get('editor');
    const nextMode = searchParams.get('mode');
    const nextFarmerId = searchParams.get('farmerId');
    const nextReportId = searchParams.get('reportId');
    const nextPostId = searchParams.get('postId');

    if (nextEditor === 'farmer' || nextEditor === 'operator') {
      setPostEditorTab(nextEditor);
    }

    if (nextMode === 'create' || nextMode === 'edit') {
      if ((nextEditor || postEditorTab) === 'farmer') {
        setReportEditorMode(nextMode);
      } else {
        setPostEditorMode(nextMode);
      }
    }

    if (nextFarmerId) {
      setReportFarmerId(nextFarmerId);
    }

    if (nextReportId) {
      setTargetReportId(nextReportId);
    }

    if (nextPostId) {
      setTargetPostId(nextPostId);
    }
  }, [isAllMode, isPostsMode, searchParams]);

  useEffect(() => {
    if (!postEditorTabs.length) return;
    if (!postEditorTabs.some((tab) => tab.key === postEditorTab)) {
      setPostEditorTab(postEditorTabs[0].key);
    }
  }, [postEditorTab, postEditorTabs]);

  useEffect(() => {
    if (!targetDoctorId || doctorSubTab !== 'info') return;
    const selected = doctorOptions.find((row) => row.id === targetDoctorId);
    if (!selected) return;
    setDoctor({
      name: selected.name || '',
      title: selected.title || '',
      clinic: selected.clinic || '',
      specialty: selected.specialty || '',
      detailPagePath: selected.detailPagePath || '',
      visibility: selected.visibility || 'public',
      displayOrder: Number(selected.displayOrder) || 0,
    });
  }, [targetDoctorId, doctorSubTab, doctorOptions]);

  useEffect(() => {
    if (postEditorTab !== 'operator' || postEditorMode !== 'edit') return;
    if (!targetPostId) {
      setPost(createEmptyPost());
      setInitialPostContentHtml('<p></p>');
      setPostFarmerTagIds([]);
      setPostDoctorTagIds([]);
      return;
    }
    const selected = postOptions.find((row) => row.id === targetPostId);
    if (!selected) return;
    setPost({
      title: selected.title || '',
      publishedDate: selected.publishedDate || '',
      body: selected.body || '',
      introImageUrl: selected.introImageUrl || selected.coverImageUrl || '',
      youtubeUrl: selected.youtubeUrl || '',
      tags: (selected.tags || []).join(','),
      cropTags: (selected.cropTags || []).join(','),
      contentHtml: selected.contentHtml || convertBlocksToHtml(selected.contentBlocks || []) || '<p></p>',
      visibility: selected.visibility || 'private',
      contentBlocks: Array.isArray(selected.contentBlocks) ? selected.contentBlocks : [],
    });
    setInitialPostContentHtml(selected.contentHtml || convertBlocksToHtml(selected.contentBlocks || []) || '<p></p>');
    setPostFarmerTagIds(selected.farmerTagIds || []);
    setPostDoctorTagIds(selected.doctorTagIds || []);
    setPostPreviewChecks({ pc: false, mobile: false });
  }, [targetPostId, postEditorTab, postEditorMode, postOptions]);

  useEffect(() => {
    if (postEditorTab !== 'operator' || postEditorMode !== 'create') return;
    setTargetPostId('');
    setPost(createEmptyPost());
    setInitialPostContentHtml('<p></p>');
    setPostFarmerTagIds([]);
    setPostDoctorTagIds([]);
    setPostFarmerCandidateId('');
    setPostDoctorCandidateId('');
    setPostPreviewDevice('');
    setPostPreviewChecks({ pc: false, mobile: false });
    setPostSubmitStatus(null);
  }, [postEditorTab, postEditorMode]);

  useEffect(() => {
    async function loadReportOptions() {
      if (postEditorTab !== 'farmer' || reportEditorMode !== 'edit' || !reportFarmerId) {
        setReportOptions([]);
        return;
      }
      const rows = await listFarmerMonthlyReports(reportFarmerId, true);
      setReportOptions(rows);
    }
    loadReportOptions();
  }, [postEditorTab, reportEditorMode, reportFarmerId]);

  useEffect(() => {
    if (postEditorTab !== 'farmer' || reportEditorMode !== 'edit') return;
    if (!targetReportId) {
      setReport(emptyReport);
      setInitialReportContentHtml('<p></p>');
      return;
    }
    const selected = reportOptions.find((row) => row.id === targetReportId);
    if (!selected) return;
    const selectedContentHtml = selected.contentHtml || convertPlainTextToHtml(selected.body || '');
    setReport({
      title: selected.title || selected.highlights || '',
      publishedDate: selected.publishedDate || selected.month || '',
      body: selected.body || '',
      contentHtml: selectedContentHtml,
      introImageUrl: selected.introImageUrl || selected.coverImageUrl || '',
      visibility: selected.visibility || 'private',
      photos: Array.isArray(selected.photos) ? selected.photos : [],
      tags: (selected.tags || selected.cropTags || []).join(','),
    });
    setInitialReportContentHtml(selectedContentHtml);
  }, [postEditorTab, reportEditorMode, targetReportId, reportOptions]);

  useEffect(() => {
    if (postEditorTab !== 'farmer' || reportEditorMode !== 'create') return;
    setTargetReportId('');
    setReportOptions([]);
    setReport(emptyReport);
    setInitialReportContentHtml('<p></p>');
  }, [postEditorTab, reportEditorMode]);

  useEffect(() => {
    if (!targetPostId && postEditorTab === 'operator') {
      setPost(createEmptyPost());
      setInitialPostContentHtml('<p></p>');
      setPostFarmerTagIds([]);
      setPostDoctorTagIds([]);
      setPostPreviewChecks({ pc: false, mobile: false });
      setPostSubmitStatus(null);
      return;
    }
  }, [targetPostId, postEditorTab]);

  useEffect(() => {
    if (postEditorTab !== 'operator') return;
    if (!postContentImageOptions.length) return;
    if (postContentImageOptions.length === 1) {
      const onlyImage = postContentImageOptions[0];
      if (post.introImageUrl !== onlyImage) {
        setPost((prev) => ({ ...prev, introImageUrl: onlyImage }));
      }
      return;
    }
    if (!post.introImageUrl || !postContentImageOptions.includes(post.introImageUrl)) {
      setPost((prev) => ({ ...prev, introImageUrl: postContentImageOptions[0] }));
    }
  }, [post.introImageUrl, postContentImageOptions, postEditorTab]);

  useEffect(() => {
    if (postEditorTab !== 'farmer') return;
    if (!reportContentImageOptions.length) {
      if (report.introImageUrl) {
        setReport((prev) => ({ ...prev, introImageUrl: '' }));
      }
      return;
    }
    if (reportContentImageOptions.length === 1) {
      const onlyImage = reportContentImageOptions[0];
      if (report.introImageUrl !== onlyImage) {
        setReport((prev) => ({ ...prev, introImageUrl: onlyImage }));
      }
      return;
    }
    if (!report.introImageUrl || !reportContentImageOptions.includes(report.introImageUrl)) {
      setReport((prev) => ({ ...prev, introImageUrl: reportContentImageOptions[0] }));
    }
  }, [postEditorTab, report.introImageUrl, reportContentImageOptions]);

  async function handleCreateFarmer(e) {
    e.preventDefault();
    setFarmerCreateStatus(null);
    try {
      await createFarmer(
        {
          ...normalizeFarmerPayload(farmer),
          gallery: [],
        },
        user.uid
      );
      setStatus('農家を作成しました。');
      setFarmerOptions(await listFarmers());
      setFarmer(emptyFarmer);
      setFarmerCreateStatus({ type: 'success', text: '農家を作成しました。' });
    } catch (error) {
      setFarmerCreateStatus({ type: 'error', text: `農家の作成に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
    }
  }

  async function handleUpdateFarmer(e) {
    e.preventDefault();
    await updateFarmer(targetFarmerId, normalizeFarmerPayload(farmer));
    setStatus('農家情報を更新しました。');
    setFarmerOptions(await listFarmers());
  }

  async function handleCreateDoctor(e) {
    e.preventDefault();
    setDoctorCreateStatus(null);
    try {
      await createDoctor({
        ...doctor,
        displayOrder: Number(doctor.displayOrder) || 0,
      }, user.uid);
      setStatus('ドクターを作成しました。');
      setDoctorOptions(await listDoctors());
      setDoctor(emptyDoctor);
      setDoctorCreateStatus({ type: 'success', text: 'ドクターを作成しました。' });
    } catch (error) {
      setDoctorCreateStatus({ type: 'error', text: `ドクターの作成に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
    }
  }

  async function handleUpdateDoctor(e) {
    e.preventDefault();
    await updateDoctor(targetDoctorId, {
      ...doctor,
      displayOrder: Number(doctor.displayOrder) || 0,
    });
    setStatus('ドクター情報を更新しました。');
    setDoctorOptions(await listDoctors());
  }

  async function handleCreateSoil(e) {
    e.preventDefault();
    await createSoilTest(targetFarmerId, getSoilPayload(), user.uid);
    setStatus('土壌分析データを登録しました。');
  }

  async function handleUpdateSoil(e) {
    e.preventDefault();
    await updateSoilTest(targetFarmerId, targetSoilId, getSoilPayload());
    setStatus('土壌分析データを更新しました。');
  }

  async function handleCreateReport(e) {
    e.preventDefault();
    setReportSubmitStatus(null);
    try {
      const payload = await getPreparedReportPayload();
      await createMonthlyReport(reportFarmerId, {
        ...payload,
        farmerTagIds: [reportFarmerId],
        approvalStatus: 'draft',
        visibility: 'private',
      }, user.uid);
      setReport(emptyReport);
      setInitialReportContentHtml('<p></p>');
      setReportSubmitStatus({ type: 'success', text: '農家近況報告の下書きを作成しました。' });
    } catch (error) {
      setReportSubmitStatus({ type: 'error', text: `農家近況報告の下書き作成に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
    }
  }

  async function handleUpdateReport(e) {
    e.preventDefault();
    setReportSubmitStatus(null);
    try {
      const payload = await getPreparedReportPayload();
      await updateMonthlyReport(reportFarmerId, targetReportId, payload);
      setInitialReportContentHtml(payload.contentHtml);
      setReportOptions(await listFarmerMonthlyReports(reportFarmerId, true));
      setReportSubmitStatus({ type: 'success', text: '農家近況報告を更新しました。' });
    } catch (error) {
      setReportSubmitStatus({ type: 'error', text: `農家近況報告の更新に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
    }
  }

  async function handleCreateReportAndSubmitReview() {
    setReportSubmitStatus(null);
    setReportSubmitDebug(buildSubmitDebug('farmer-report', 'create', 'submitting', {
      farmerId: reportFarmerId,
      reportId: '(new)',
    }));
    try {
      const payload = await getPreparedReportPayload();
      const created = await createMonthlyReport(reportFarmerId, {
        ...payload,
        farmerTagIds: [reportFarmerId],
        approvalStatus: 'draft',
        visibility: 'private',
      }, user.uid);
      await submitMonthlyReportForReview(reportFarmerId, created.id, user.uid);
      setReportOptions(await listFarmerMonthlyReports(reportFarmerId, true));
      setTargetReportId(created.id);
      setInitialReportContentHtml(payload.contentHtml);
      setReportSubmitStatus({ type: 'success', text: '投稿申請が完了しました。しばらくお待ちください。' });
      setReportSubmitDebug(buildSubmitDebug('farmer-report', 'create', 'success', {
        farmerId: reportFarmerId,
        reportId: created.id,
      }));
      setReportEditorMode('edit');
    } catch (error) {
      setReportSubmitStatus({ type: 'error', text: `申請に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
      setReportSubmitDebug(buildSubmitDebug('farmer-report', 'create', 'error', {
        farmerId: reportFarmerId,
        reportId: '(new)',
        errorCode: error?.code || '',
        errorMessage: error?.message || '',
      }));
    }
  }

  async function handleSubmitReportReview() {
    if (!targetReportId) return;
    setReportSubmitStatus(null);
    setReportSubmitDebug(buildSubmitDebug('farmer-report', 'edit', 'submitting', {
      farmerId: reportFarmerId,
      reportId: targetReportId,
    }));
    try {
      const payload = await getPreparedReportPayload();
      const selected = reportOptions.find((row) => row.id === targetReportId) || {};
      const pendingRemovedImagePublicIds = getRemovedPostImagePublicIds(selected.contentHtml || initialReportContentHtml, payload.contentHtml);
      const pendingChangeSummary = summarizeRecordChanges(selected, payload, [
        ['タイトル', selected.title || selected.highlights || '', payload.title || ''],
        ['日付', selected.publishedDate || selected.month || '', payload.publishedDate || payload.month || ''],
        ['タグ', normalizeTagList(selected.tags || selected.cropTags || []), normalizeTagList(payload.tags || payload.cropTags || [])],
        ['冒頭画像', selected.introImageUrl || selected.coverImageUrl || '', payload.introImageUrl || ''],
      ]);
      await updateMonthlyReport(reportFarmerId, targetReportId, {
        ...payload,
        approvalStatus: 'draft',
        visibility: 'private',
        pendingRemovedImagePublicIds,
        pendingChangeSummary,
      });
      await submitMonthlyReportForReview(reportFarmerId, targetReportId, user.uid);
      setInitialReportContentHtml(payload.contentHtml);
      setReportOptions(await listFarmerMonthlyReports(reportFarmerId, true));
      setReportSubmitStatus({ type: 'success', text: '投稿申請が完了しました。しばらくお待ちください。' });
      setReportSubmitDebug(buildSubmitDebug('farmer-report', 'edit', 'success', {
        farmerId: reportFarmerId,
        reportId: targetReportId,
      }));
    } catch (error) {
      setReportSubmitStatus({ type: 'error', text: `申請に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
      setReportSubmitDebug(buildSubmitDebug('farmer-report', 'edit', 'error', {
        farmerId: reportFarmerId,
        reportId: targetReportId,
        errorCode: error?.code || '',
        errorMessage: error?.message || '',
      }));
    }
  }

  async function handleCreatePost(e) {
    e.preventDefault();
    setPostSubmitStatus(null);
    try {
      const payload = await getPreparedPostPayload();
      const created = await createPost({ ...payload, approvalStatus: 'draft', visibility: 'private' }, user.uid);
      const refreshed = await listPostsForAdmin();
      setPostOptions(refreshed);
      setTargetPostId(created.id);
      setPost(createEmptyPost());
      setInitialPostContentHtml('<p></p>');
      setPostFarmerTagIds([]);
      setPostDoctorTagIds([]);
      setPostEditorMode('edit');
      setPostSubmitStatus({ type: 'success', text: '運営記事の下書きを作成しました。' });
    } catch (error) {
      setPostSubmitStatus({ type: 'error', text: `運営記事の下書き作成に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
    }
  }

  async function handleCreatePostAndSubmitReview() {
    if (!canSubmitPost) {
      setPostSubmitStatus({ type: 'error', text: 'プレビューを確認してください。' });
      setPostSubmitDebug(buildSubmitDebug('operator-post', 'create', 'blocked', {
        postId: '(new)',
        canSubmitPost,
        previewChecks: postPreviewChecks,
        reason: 'preview-required',
      }));
      return;
    }
    setPostSubmitStatus(null);
    setPostSubmitDebug(buildSubmitDebug('operator-post', 'create', 'submitting', {
      postId: '(new)',
      canSubmitPost,
      previewChecks: postPreviewChecks,
    }));
    try {
      const payload = await getPreparedPostPayload();
      const created = await createPost({ ...payload, approvalStatus: 'draft', visibility: 'private' }, user.uid);
      await submitPostForReview(created.id, user.uid);
      const refreshed = await listPostsForAdmin();
      setPostOptions(refreshed);
      setTargetPostId(created.id);
      setInitialPostContentHtml(payload.contentHtml);
      setPostEditorMode('edit');
      setPostSubmitStatus({ type: 'success', text: '投稿申請が完了しました。しばらくお待ちください。' });
      setPostSubmitDebug(buildSubmitDebug('operator-post', 'create', 'success', {
        postId: created.id,
        canSubmitPost,
        previewChecks: postPreviewChecks,
      }));
    } catch (error) {
      setPostSubmitStatus({ type: 'error', text: `申請に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
      setPostSubmitDebug(buildSubmitDebug('operator-post', 'create', 'error', {
        postId: '(new)',
        canSubmitPost,
        previewChecks: postPreviewChecks,
        errorCode: error?.code || '',
        errorMessage: error?.message || '',
      }));
    }
  }

  async function handleUpdatePost(e) {
    e.preventDefault();
    setPostSubmitStatus(null);
    try {
      const payload = await getPreparedPostPayload();
      await updatePost(targetPostId, payload);
      setInitialPostContentHtml(payload.contentHtml);
      setPostOptions(await listPostsForAdmin());
      setPostSubmitStatus({ type: 'success', text: '運営記事を更新しました。' });
    } catch (error) {
      setPostSubmitStatus({ type: 'error', text: `運営記事の更新に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
    }
  }

  async function handleSubmitPostReview() {
    if (!targetPostId) return;
    if (!canSubmitPost) {
      setPostSubmitStatus({ type: 'error', text: 'プレビューを確認してください。' });
      setPostSubmitDebug(buildSubmitDebug('operator-post', 'edit', 'blocked', {
        postId: targetPostId,
        canSubmitPost,
        previewChecks: postPreviewChecks,
        reason: 'preview-required',
      }));
      return;
    }
    setPostSubmitStatus(null);
    setPostSubmitDebug(buildSubmitDebug('operator-post', 'edit', 'submitting', {
      postId: targetPostId,
      canSubmitPost,
      previewChecks: postPreviewChecks,
    }));
    try {
      const payload = await getPreparedPostPayload();
      const selected = postOptions.find((row) => row.id === targetPostId) || {};
      const pendingRemovedImagePublicIds = getRemovedPostImagePublicIds(selected.contentHtml || initialPostContentHtml, payload.contentHtml);
      const pendingChangeSummary = summarizeRecordChanges(selected, payload, [
        ['タイトル', selected.title || '', payload.title || ''],
        ['日付', selected.publishedDate || '', payload.publishedDate || ''],
        ['リード文', selected.body || '', payload.body || ''],
        ['記事タグ', normalizeTagList(selected.tags || []), normalizeTagList(payload.tags || [])],
        ['野菜タグ', normalizeTagList(selected.cropTags || []), normalizeTagList(payload.cropTags || [])],
        ['対象農家', normalizeTagList(selected.farmerTagIds || []), normalizeTagList(payload.farmerTagIds || [])],
        ['対象ドクター', normalizeTagList(selected.doctorTagIds || []), normalizeTagList(payload.doctorTagIds || [])],
        ['冒頭画像', selected.introImageUrl || '', payload.introImageUrl || ''],
      ]);
      await updatePost(targetPostId, {
        ...payload,
        approvalStatus: 'draft',
        visibility: 'private',
        pendingRemovedImagePublicIds,
        pendingChangeSummary,
      });
      setInitialPostContentHtml(payload.contentHtml);
      await submitPostForReview(targetPostId, user.uid);
      setPostSubmitStatus({ type: 'success', text: '投稿申請が完了しました。しばらくお待ちください。' });
      setPostSubmitDebug(buildSubmitDebug('operator-post', 'edit', 'success', {
        postId: targetPostId,
        canSubmitPost,
        previewChecks: postPreviewChecks,
      }));
    } catch (error) {
      setPostSubmitStatus({ type: 'error', text: `申請に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
      setPostSubmitDebug(buildSubmitDebug('operator-post', 'edit', 'error', {
        postId: targetPostId,
        canSubmitPost,
        previewChecks: postPreviewChecks,
        errorCode: error?.code || '',
        errorMessage: error?.message || '',
      }));
    }
  }

  function handleAddPostFarmerTag() {
    if (!postFarmerCandidateId) return;
    if (postFarmerTagIds.includes(postFarmerCandidateId)) return;
    setPostFarmerTagIds((prev) => [...prev, postFarmerCandidateId]);
    markPostPreviewStale();
  }

  function handleRemovePostFarmerTag(farmerId) {
    setPostFarmerTagIds((prev) => prev.filter((id) => id !== farmerId));
    markPostPreviewStale();
  }

  function handleAddPostDoctorTag() {
    if (!postDoctorCandidateId) return;
    if (postDoctorTagIds.includes(postDoctorCandidateId)) return;
    setPostDoctorTagIds((prev) => [...prev, postDoctorCandidateId]);
    markPostPreviewStale();
  }

  function handleRemovePostDoctorTag(doctorId) {
    setPostDoctorTagIds((prev) => prev.filter((id) => id !== doctorId));
    markPostPreviewStale();
  }

  function updatePostField(field, value) {
    markPostPreviewStale();
    setPost((prev) => ({ ...prev, [field]: value }));
  }

  function markPostPreviewStale() {
    setPostPreviewChecks({ pc: false, mobile: false });
    setPostSubmitStatus(null);
  }

  function handleTogglePostPreview(device) {
    setPostPreviewDevice((prev) => prev === device ? '' : device);
    setPostPreviewChecks((prev) => ({ ...prev, [device]: true }));
  }

  async function handleCheckFarmerPassword(event) {
    event.preventDefault();
    setFarmerPasswordCheckResult(null);
    setFarmerPasswordCheckError('');
    if (!isAdmin) {
      setFarmerPasswordCheckError('この操作はサイト管理者のみ実行できます。');
      return;
    }
    if (!farmerPasswordUid) {
      setFarmerPasswordCheckError('確認対象アカウントを選択してください。');
      return;
    }

    setFarmerPasswordCheckBusy(true);
    try {
      const row = await getUserProfile(farmerPasswordUid);
      if (!row) {
        setFarmerPasswordCheckError('対象ユーザーのプロフィールが見つかりません。');
        return;
      }
      const decryptedPassword = decryptPasswordOnClient(row, farmerCipherKey);
      setFarmerPasswordCheckResult({
        uid: farmerPasswordUid,
        name: row.name || '-',
        email: row.email || '-',
        role: row.role || '-',
        encryptedAt: row.passwordEncryptedAt || '-',
        passwordPlainText: decryptedPassword,
      });

      await logPasswordView({
        actorUid: user.uid,
        actorEmail: user.email || '',
        targetUid: farmerPasswordUid,
        targetEmail: row.email || '',
        targetRole: row.role || '',
        scope: 'admin_farmers',
      });
      await cleanupExpiredPasswordViewLogs();
      const logs = await listPasswordViewLogs(120);
      setFarmerViewLogs(logs.filter((item) => item.scope === 'admin_farmers'));
    } catch (err) {
      setFarmerPasswordCheckError(err?.message === 'invalid-cipher-key' ? '復号キーが正しくありません。' : 'パスワード復号に失敗しました。');
    } finally {
      setFarmerPasswordCheckBusy(false);
    }
  }

  function getSoilPayload() {
    return {
      date: soil.date,
      sampleLocation: soil.sampleLocation,
      labName: soil.labName,
      notes: '',
      metrics: {
        pH: Number(soil.pH) || null,
        EC: Number(soil.EC) || null,
        N: Number(soil.N) || null,
        P: Number(soil.P) || null,
        K: Number(soil.K) || null,
      },
      pdfEmbedUrl: soil.pdfEmbedUrl,
    };
  }

  async function getPreparedReportPayload() {
    const tags = (report.tags || '').split(',').map((v) => v.trim()).filter(Boolean);
    const previousImageUrls = extractImageUrlsFromHtml(report.contentHtml || '<p></p>');
    const editorHtml = await reportEditorRef.current?.persistPendingImages?.();
    const contentHtml = (editorHtml || report.contentHtml || '<p></p>').trim();
    const nextImageUrls = extractImageUrlsFromHtml(contentHtml);
    const introImageUrl = resolvePersistedIntroImageUrl(report.introImageUrl, previousImageUrls, nextImageUrls);
    const autoCoverImage = introImageUrl
      || extractFirstImageFromHtml(contentHtml)
      || '';
    const bodyText = extractPlainTextFromHtml(contentHtml);
    return {
      ...report,
      title: (report.title || '').trim(),
      publishedDate: report.publishedDate || '',
      body: bodyText,
      contentHtml,
      introImageUrl,
      coverImageUrl: autoCoverImage,
      tags,
      month: report.publishedDate || '',
      highlights: (report.title || '').trim(),
      cropTags: tags,
    };
  }

  async function getPreparedPostPayload() {
    const previousImageUrls = extractImageUrlsFromHtml(post.contentHtml || '<p></p>');
    const editorHtml = await postEditorRef.current?.persistPendingImages?.();
    const contentHtml = (editorHtml || post.contentHtml || '<p></p>').trim();
    const nextImageUrls = extractImageUrlsFromHtml(contentHtml);
    const introImageUrl = resolvePersistedIntroImageUrl(post.introImageUrl, previousImageUrls, nextImageUrls);
    const autoCoverImage = introImageUrl
      || extractFirstImageFromHtml(contentHtml)
      || '';

    return {
      title: (post.title || '').trim(),
      slug: slugifyTitle(post.title),
      publishedDate: post.publishedDate || '',
      body: (post.body || '').trim(),
      introImageUrl,
      coverImageUrl: autoCoverImage,
      youtubeUrl: (post.youtubeUrl || '').trim(),
      tags: post.tags.split(',').map((v) => v.trim()).filter(Boolean),
      cropTags: post.cropTags.split(',').map((v) => v.trim()).filter(Boolean),
      farmerTagIds: postFarmerTagIds,
      doctorTagIds: postDoctorTagIds,
      contentHtml,
      contentBlocks: [],
      visibility: 'private',
    };
  }

  const postPreviewHtml = sanitizeArticleHtml(post.contentHtml || '<p></p>');
  const canSubmitPost = postPreviewChecks.pc && postPreviewChecks.mobile;

  return (
    <>
      <Helmet><title>{pageTitle}</title></Helmet>
      <h1>{isFarmersMode ? '農家関連' : isDoctorsMode ? 'Drs. Farmドクター関連' : isPostsMode ? '記事作成' : '管理'}</h1>
      <p className="hint">ログイン権限: {dashboardRoleLabel}</p>
      {status && <p className="success-text">{status}</p>}

      {!isAllMode ? (
        <section className="panel">
          <div className="row-actions">
            <Link className="btn btn-secondary" to="/admin">管理者メニューへ戻る</Link>
            {isFarmersMode ? (
              <>
                <button type="button" className={`btn ${farmerSubTab === 'create' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFarmerSubTab('create')}>新規農家作成</button>
                <button type="button" className={`btn ${farmerSubTab === 'info' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFarmerSubTab('info')}>農家情報</button>
                <button type="button" className={`btn ${farmerSubTab === 'password' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFarmerSubTab('password')}>農家パスワード確認</button>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      {(isAllMode || isFarmersMode) ? (
        <section className="admin-grid">
          <article className="panel form-grid">
            {isAllMode ? (
              <div className="row-actions">
                <button type="button" className={`btn ${farmerSubTab === 'create' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFarmerSubTab('create')}>新規農家作成</button>
                <button type="button" className={`btn ${farmerSubTab === 'info' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFarmerSubTab('info')}>農家情報</button>
                <button type="button" className={`btn ${farmerSubTab === 'password' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFarmerSubTab('password')}>農家パスワード確認</button>
              </div>
            ) : null}

            {farmerSubTab === 'create' ? (
              <form id="operator-farmers-create" onSubmit={handleCreateFarmer} className="form-grid">
                <h2>新規農家作成</h2>
                <label>名前<input value={farmer.name} onChange={(e) => setFarmer({ ...farmer, name: e.target.value })} required /></label>
                <label>slug<input value={farmer.slug} onChange={(e) => setFarmer({ ...farmer, slug: e.target.value })} required /></label>
                <label>都道府県<input value={farmer.prefecture} onChange={(e) => setFarmer({ ...farmer, prefecture: e.target.value })} required /></label>
                <label>市区町村<input value={farmer.city} onChange={(e) => setFarmer({ ...farmer, city: e.target.value })} required /></label>
                <label>圃場住所・表示エリア<input value={farmer.fieldAddress} onChange={(e) => setFarmer({ ...farmer, fieldAddress: e.target.value })} placeholder="例: 愛知県瀬戸市上半田川町" /></label>
                <label>周辺環境メモ<textarea value={farmer.environmentText} onChange={(e) => setFarmer({ ...farmer, environmentText: e.target.value })} rows="3" /></label>
                <label>Google Maps共有URL<input value={farmer.mapUrl} onChange={(e) => setFarmer({ ...farmer, mapUrl: e.target.value })} placeholder="https://maps.app.goo.gl/..." /></label>
                <div className="form-two-col">
                  <label>緯度<input value={farmer.geoLat} onChange={(e) => setFarmer({ ...farmer, geoLat: e.target.value })} placeholder="35.000000" inputMode="decimal" /></label>
                  <label>経度<input value={farmer.geoLng} onChange={(e) => setFarmer({ ...farmer, geoLng: e.target.value })} placeholder="136.000000" inputMode="decimal" /></label>
                </div>
                <label>作物(カンマ区切り)<input value={farmer.crops} onChange={(e) => setFarmer({ ...farmer, crops: e.target.value })} /></label>
                <label>ownerUid<input value={farmer.ownerUid} onChange={(e) => setFarmer({ ...farmer, ownerUid: e.target.value })} /></label>
                <label>ヒーロー画像URL<input value={farmer.heroImageUrl} onChange={(e) => setFarmer({ ...farmer, heroImageUrl: e.target.value })} /></label>
                <label>YouTube URL<input value={farmer.youtubeUrl} onChange={(e) => setFarmer({ ...farmer, youtubeUrl: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." /></label>
                <button className="btn" type="submit">作成</button>
                {farmerCreateStatus ? (
                  <p className={farmerCreateStatus.type === 'error' ? 'error-text' : 'success-text'}>{farmerCreateStatus.text}</p>
                ) : null}
              </form>
            ) : null}

            {farmerSubTab === 'info' ? (
              <div className="form-grid">
                <form id="operator-farmers-info" onSubmit={handleUpdateFarmer} className="form-grid">
                  <h2>農家情報</h2>
                  <label>更新/削除対象 farmerId
                    <select value={targetFarmerId} onChange={(e) => setTargetFarmerId(e.target.value)} required>
                      <option value="">選択してください</option>
                      {farmerOptions.map((row) => (
                        <option key={row.id} value={row.id}>{row.name} / {row.prefecture} {row.city}</option>
                      ))}
                    </select>
                  </label>
                  <label>名前<input value={farmer.name} onChange={(e) => setFarmer({ ...farmer, name: e.target.value })} required /></label>
                  <label>slug<input value={farmer.slug} onChange={(e) => setFarmer({ ...farmer, slug: e.target.value })} required /></label>
                  <label>都道府県<input value={farmer.prefecture} onChange={(e) => setFarmer({ ...farmer, prefecture: e.target.value })} required /></label>
                  <label>市区町村<input value={farmer.city} onChange={(e) => setFarmer({ ...farmer, city: e.target.value })} required /></label>
                  <label>圃場住所・表示エリア<input value={farmer.fieldAddress} onChange={(e) => setFarmer({ ...farmer, fieldAddress: e.target.value })} placeholder="例: 愛知県瀬戸市上半田川町" /></label>
                  <label>周辺環境メモ<textarea value={farmer.environmentText} onChange={(e) => setFarmer({ ...farmer, environmentText: e.target.value })} rows="3" /></label>
                  <label>Google Maps共有URL<input value={farmer.mapUrl} onChange={(e) => setFarmer({ ...farmer, mapUrl: e.target.value })} placeholder="https://maps.app.goo.gl/..." /></label>
                  <div className="form-two-col">
                    <label>緯度<input value={farmer.geoLat} onChange={(e) => setFarmer({ ...farmer, geoLat: e.target.value })} placeholder="35.000000" inputMode="decimal" /></label>
                    <label>経度<input value={farmer.geoLng} onChange={(e) => setFarmer({ ...farmer, geoLng: e.target.value })} placeholder="136.000000" inputMode="decimal" /></label>
                  </div>
                  <label>作物(カンマ区切り)<input value={farmer.crops} onChange={(e) => setFarmer({ ...farmer, crops: e.target.value })} /></label>
                  <label>ownerUid<input value={farmer.ownerUid} onChange={(e) => setFarmer({ ...farmer, ownerUid: e.target.value })} /></label>
                  <label>ヒーロー画像URL<input value={farmer.heroImageUrl} onChange={(e) => setFarmer({ ...farmer, heroImageUrl: e.target.value })} /></label>
                  <label>YouTube URL<input value={farmer.youtubeUrl} onChange={(e) => setFarmer({ ...farmer, youtubeUrl: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." /></label>
                  <div className="row-actions">
                    <button className="btn btn-outline-solid" type="submit">更新</button>
                    <button className="btn btn-danger" type="button" onClick={() => deleteFarmer(targetFarmerId).then(async () => {
                      setStatus('農家を削除しました。');
                      setTargetFarmerId('');
                      setFarmerOptions(await listFarmers());
                    })} disabled={!targetFarmerId}>削除</button>
                  </div>
                </form>

                <form onSubmit={handleCreateSoil} className="form-grid">
                  <h2>土壌分析データ</h2>
                  <label>対象farmerId
                    <select value={targetFarmerId} onChange={(e) => setTargetFarmerId(e.target.value)} required>
                      <option value="">選択してください</option>
                      {farmerOptions.map((row) => (
                        <option key={row.id} value={row.id}>{row.name} / {row.prefecture} {row.city}</option>
                      ))}
                    </select>
                  </label>
                  <label>soilTestId(更新/削除時)<input value={targetSoilId} onChange={(e) => setTargetSoilId(e.target.value)} /></label>
                  <label>採取日<input type="date" value={soil.date} onChange={(e) => setSoil({ ...soil, date: e.target.value })} required /></label>
                  <label>採取地点<input value={soil.sampleLocation} onChange={(e) => setSoil({ ...soil, sampleLocation: e.target.value })} /></label>
                  <label>分析機関<input value={soil.labName} onChange={(e) => setSoil({ ...soil, labName: e.target.value })} /></label>
                  <label>pH<input value={soil.pH} onChange={(e) => setSoil({ ...soil, pH: e.target.value })} /></label>
                  <label>EC<input value={soil.EC} onChange={(e) => setSoil({ ...soil, EC: e.target.value })} /></label>
                  <label>N<input value={soil.N} onChange={(e) => setSoil({ ...soil, N: e.target.value })} /></label>
                  <label>P<input value={soil.P} onChange={(e) => setSoil({ ...soil, P: e.target.value })} /></label>
                  <label>K<input value={soil.K} onChange={(e) => setSoil({ ...soil, K: e.target.value })} /></label>
                  <label>OneDrive embed URL<input value={soil.pdfEmbedUrl} onChange={(e) => setSoil({ ...soil, pdfEmbedUrl: e.target.value })} /></label>
                  <div className="row-actions">
                    <button className="btn" type="submit">作成</button>
                    <button className="btn btn-outline-solid" type="button" onClick={handleUpdateSoil} disabled={!targetSoilId}>更新</button>
                    <button className="btn btn-danger" type="button" onClick={() => deleteSoilTest(targetFarmerId, targetSoilId).then(() => setStatus('土壌分析を削除しました。'))} disabled={!targetSoilId}>削除</button>
                  </div>
                </form>
              </div>
            ) : null}

            {farmerSubTab === 'password' ? (
              <form className="form-grid" onSubmit={handleCheckFarmerPassword}>
                <h2>農家パスワード確認</h2>
                {isAdmin ? (
                  <>
                    <p className="hint">農家アカウントを選択し、復号キー入力後にパスワード確認できます。</p>
                    <label>確認対象アカウント
                      <select value={farmerPasswordUid} onChange={(e) => setFarmerPasswordUid(e.target.value)} required>
                        <option value="">選択してください</option>
                        {farmerUserOptions.map((row) => (
                          <option key={row.uid} value={row.uid}>{row.email || row.uid} / {row.name || '-'}</option>
                        ))}
                      </select>
                    </label>
                    <label>復号キー
                      <input type="password" value={farmerCipherKey} onChange={(e) => setFarmerCipherKey(e.target.value)} placeholder={PASSWORD_HINT} required />
                    </label>
                    <button type="submit" className="btn btn-secondary" disabled={farmerPasswordCheckBusy}>{farmerPasswordCheckBusy ? '確認中...' : '復号して確認'}</button>
                    {farmerPasswordCheckError ? <p className="error-text">{farmerPasswordCheckError}</p> : null}
                    {farmerPasswordCheckResult ? (
                      <div className="hint">
                        <p>UID: {farmerPasswordCheckResult.uid}</p>
                        <p>氏名: {farmerPasswordCheckResult.name}</p>
                        <p>メール: {farmerPasswordCheckResult.email}</p>
                        <p>権限: {farmerPasswordCheckResult.role}</p>
                        <p>暗号化日時: {farmerPasswordCheckResult.encryptedAt}</p>
                        <p><strong>パスワード: {farmerPasswordCheckResult.passwordPlainText}</strong></p>
                      </div>
                    ) : null}
                    <h3>パスワード閲覧ログ（半年分）</h3>
                    {farmerViewLogs.length === 0 ? <p className="hint">ログはまだありません。</p> : (
                      <ul className="plain-list">
                        {farmerViewLogs.map((log) => (
                          <li key={log.id} className="list-row">
                            <p>{typeof log?.viewedAt?.toDate === 'function' ? log.viewedAt.toDate().toLocaleString('ja-JP') : '-'} / 閲覧者: {log.actorEmail || log.actorUid} / 対象: {log.targetEmail || log.targetUid}</p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p>この項目はサイト管理者のみ利用できます。</p>
                )}
              </form>
            ) : null}
          </article>
        </section>
      ) : null}

      {(isAllMode || isDoctorsMode) ? (
        <section className="admin-grid">
          <article className="panel form-grid">
            <div className="row-actions">
              <button type="button" className={`btn ${doctorSubTab === 'create' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDoctorSubTab('create')}>新規ドクター作成</button>
              <button type="button" className={`btn ${doctorSubTab === 'info' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDoctorSubTab('info')}>ドクター詳細</button>
            </div>

            {doctorSubTab === 'create' ? (
              <form onSubmit={handleCreateDoctor} className="form-grid">
                <h2>新規ドクター作成</h2>
                <p className="hint">ここでは一覧表示に必要な基本情報と、Hosting 側の詳細ページへの紐付け先を登録します。紹介文や画像など、詳細ページに出す情報は Hosting 側の固定ページで管理します。</p>
                <label>氏名<input value={doctor.name} onChange={(e) => setDoctor({ ...doctor, name: e.target.value })} required /></label>
                <label>役職<input value={doctor.title} onChange={(e) => setDoctor({ ...doctor, title: e.target.value })} /></label>
                <label>所属クリニック<input value={doctor.clinic} onChange={(e) => setDoctor({ ...doctor, clinic: e.target.value })} /></label>
                <label>専門分野<input value={doctor.specialty} onChange={(e) => setDoctor({ ...doctor, specialty: e.target.value })} /></label>
                <label>詳細ページパス
                  <select value={doctor.detailPagePath} onChange={(e) => setDoctor({ ...doctor, detailPagePath: e.target.value })}>
                    <option value="">選択してください</option>
                    {doctorDetailPageOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label>表示順<input type="number" value={doctor.displayOrder} onChange={(e) => setDoctor({ ...doctor, displayOrder: e.target.value })} /></label>
                <label>公開状態
                  <select value={doctor.visibility} onChange={(e) => setDoctor({ ...doctor, visibility: e.target.value })}>
                    <option value="public">公開</option>
                    <option value="private">非公開</option>
                  </select>
                </label>
                <button className="btn" type="submit">作成</button>
                {doctorCreateStatus ? (
                  <p className={doctorCreateStatus.type === 'error' ? 'error-text' : 'success-text'}>{doctorCreateStatus.text}</p>
                ) : null}
              </form>
            ) : null}

            {doctorSubTab === 'info' ? (
              <form onSubmit={handleUpdateDoctor} className="form-grid">
                <h2>ドクター詳細</h2>
                <p className="hint">ここでは一覧に必要な基本情報と、Hosting 側の詳細ページへの紐付け先を更新します。紹介文や画像など、詳細ページの中身は Hosting 側の固定ページで管理します。</p>
                <label>更新/削除対象ドクター
                  <select value={targetDoctorId} onChange={(e) => setTargetDoctorId(e.target.value)} required>
                    <option value="">選択してください</option>
                    {doctorOptions.map((row) => (
                      <option key={row.id} value={row.id}>{formatDoctorName(row.name || row.id)}</option>
                    ))}
                  </select>
                </label>
                <label>氏名<input value={doctor.name} onChange={(e) => setDoctor({ ...doctor, name: e.target.value })} required /></label>
                <label>役職<input value={doctor.title} onChange={(e) => setDoctor({ ...doctor, title: e.target.value })} /></label>
                <label>所属クリニック<input value={doctor.clinic} onChange={(e) => setDoctor({ ...doctor, clinic: e.target.value })} /></label>
                <label>専門分野<input value={doctor.specialty} onChange={(e) => setDoctor({ ...doctor, specialty: e.target.value })} /></label>
                <label>詳細ページパス
                  <select value={doctor.detailPagePath} onChange={(e) => setDoctor({ ...doctor, detailPagePath: e.target.value })}>
                    <option value="">選択してください</option>
                    {doctorDetailPageOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label>表示順<input type="number" value={doctor.displayOrder} onChange={(e) => setDoctor({ ...doctor, displayOrder: e.target.value })} /></label>
                <label>公開状態
                  <select value={doctor.visibility} onChange={(e) => setDoctor({ ...doctor, visibility: e.target.value })}>
                    <option value="public">公開</option>
                    <option value="private">非公開</option>
                  </select>
                </label>
                <div className="row-actions">
                  <button className="btn btn-outline-solid" type="submit">更新</button>
                  <button className="btn btn-danger" type="button" onClick={() => deleteDoctor(targetDoctorId).then(async () => {
                    setStatus('ドクターを削除しました。');
                    setTargetDoctorId('');
                    setDoctor(emptyDoctor);
                    setDoctorOptions(await listDoctors());
                  })} disabled={!targetDoctorId}>削除</button>
                </div>
              </form>
            ) : null}
          </article>
        </section>
      ) : null}

      {(isAllMode || isPostsMode) ? (
        <section className="admin-grid admin-grid-single">
          <article className="panel form-grid admin-panel-full">
            <div className="editor-switcher-shell">
              <div className="editor-switcher-row">
                <span className="editor-switcher-label">記事</span>
                <div className="editor-tab-group" role="tablist" aria-label="記事種別">
                  {postEditorTabs.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`editor-tab ${postEditorTab === tab.key ? 'is-active' : ''}`}
                      onClick={() => setPostEditorTab(tab.key)}
                      aria-pressed={postEditorTab === tab.key}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="editor-switcher-row">
                <span className="editor-switcher-label">操作</span>
                <div className="editor-tab-group editor-tab-group-compact" role="tablist" aria-label="操作モード">
                  <button
                    type="button"
                    className={`editor-tab ${((postEditorTab === 'farmer' ? reportEditorMode : postEditorMode) === 'create') ? 'is-active' : ''}`}
                    onClick={() => (postEditorTab === 'farmer' ? setReportEditorMode('create') : setPostEditorMode('create'))}
                    aria-pressed={(postEditorTab === 'farmer' ? reportEditorMode : postEditorMode) === 'create'}
                  >
                    新規作成
                  </button>
                  <button
                    type="button"
                    className={`editor-tab ${((postEditorTab === 'farmer' ? reportEditorMode : postEditorMode) === 'edit') ? 'is-active' : ''}`}
                    onClick={() => (postEditorTab === 'farmer' ? setReportEditorMode('edit') : setPostEditorMode('edit'))}
                    aria-pressed={(postEditorTab === 'farmer' ? reportEditorMode : postEditorMode) === 'edit'}
                  >
                    記事編集
                  </button>
                </div>
              </div>
            </div>

            {postEditorTab === 'farmer' ? (
              <div className="form-grid">
                <form onSubmit={reportEditorMode === 'create' ? handleCreateReport : handleUpdateReport} className="form-grid">
                  <h2>農家近況報告</h2>
                  <label>対象農家
                    <select value={reportFarmerId} onChange={(e) => {
                      setReportFarmerId(e.target.value);
                      setTargetReportId('');
                    }} required>
                      <option value="">選択してください</option>
                      {reportFarmerOptions.map((row) => (
                        <option key={row.id} value={row.id}>{row.name} / {row.prefecture} {row.city}</option>
                      ))}
                    </select>
                  </label>
                  {reportEditorMode === 'edit' ? (
                    <label>編集する近況報告
                      <select value={targetReportId} onChange={(e) => setTargetReportId(e.target.value)} required>
                        <option value="">選択してください</option>
                        {reportOptions.map((row) => (
                          <option key={row.id} value={row.id}>{row.publishedDate || row.month || '-'} / {row.title || row.highlights || 'タイトル未設定'}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <label>タイトル<input value={report.title} onChange={(e) => setReport({ ...report, title: e.target.value })} required /></label>
                  <label>日付<input type="date" value={report.publishedDate} onChange={(e) => setReport({ ...report, publishedDate: e.target.value })} required /></label>
                  <div className="editor-blocks">
                    <strong>本文エディタ</strong>
                    <RichArticleEditor
                      ref={reportEditorRef}
                      value={report.contentHtml}
                      onChange={(html) => setReport((prev) => ({ ...prev, contentHtml: html }))}
                      user={user}
                    />
                  </div>
                  {(reportContentImageOptions.length > 0 || report.introImageUrl) ? (
                    <label>冒頭画像
                      <select
                        value={report.introImageUrl || ''}
                        onChange={(e) => setReport((prev) => ({ ...prev, introImageUrl: e.target.value }))}
                      >
                        {reportIntroImageOptions.map((imageUrl, index) => (
                          <option key={imageUrl || 'none'} value={imageUrl}>{imageUrl ? `本文画像 ${index}` : 'なし'}</option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <p className="hint post-editor-note">本文画像がまだないため、冒頭画像の選択は表示していません。</p>
                  )}
                  <label>タグ(カンマ区切り)<input value={report.tags} onChange={(e) => setReport({ ...report, tags: e.target.value })} /></label>
                  <div className="row-actions">
                    {reportEditorMode === 'create' ? (
                      <>
                        <button className="btn btn-outline-solid" type="submit">下書き保存</button>
                        <div className="action-feedback-stack">
                          <button className="btn btn-secondary" type="button" onClick={handleCreateReportAndSubmitReview} disabled={!reportFarmerId}>投稿申請</button>
                          {reportSubmitStatus ? (
                            <p className={reportSubmitStatus.type === 'success' ? 'success-text' : 'error-text'}>
                              {reportSubmitStatus.text}
                            </p>
                          ) : null}
                          {showSubmitDebug && reportSubmitDebug ? (
                            <pre className="action-debug-panel">{JSON.stringify(reportSubmitDebug, null, 2)}</pre>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-outline-solid" type="submit" disabled={!targetReportId}>下書きを更新</button>
                        <div className="action-feedback-stack">
                          <button className="btn btn-secondary" type="button" onClick={handleSubmitReportReview} disabled={!targetReportId}>投稿申請</button>
                          {reportSubmitStatus ? (
                            <p className={reportSubmitStatus.type === 'success' ? 'success-text' : 'error-text'}>
                              {reportSubmitStatus.text}
                            </p>
                          ) : null}
                          {showSubmitDebug && reportSubmitDebug ? (
                            <pre className="action-debug-panel">{JSON.stringify(reportSubmitDebug, null, 2)}</pre>
                          ) : null}
                        </div>
                        <button className="btn btn-danger" type="button" onClick={async () => {
                          if (!window.confirm('農家近況報告を削除しますか？この操作は元に戻せません。')) return;
                          setReportSubmitStatus(null);
                          try {
                            await deleteMonthlyReport(reportFarmerId, targetReportId);
                            setTargetReportId('');
                            setReport(emptyReport);
                            setReportOptions(await listFarmerMonthlyReports(reportFarmerId, true));
                            setReportSubmitStatus({ type: 'success', text: '農家近況報告を削除しました。' });
                          } catch (error) {
                            setReportSubmitStatus({ type: 'error', text: `農家近況報告の削除に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
                          }
                        }} disabled={!targetReportId}>削除</button>
                      </>
                    )}
                  </div>
                  <p className="hint">公開承認は「記事承認」ページで実施します。</p>
                </form>
              </div>
            ) : (
              <div className="form-grid">
                <form id="operator-posts" onSubmit={postEditorMode === 'create' ? handleCreatePost : handleUpdatePost} className="form-grid">
                  <h2>運営記事</h2>
                  <div className="hint post-editor-guide">
                    <p><strong>記事の組み方</strong></p>
                    <p>タイトル、日付、リード文、タグ、対象農家、対象ドクターを登録したあと、本文エディタでそのまま見た目を整えます。</p>
                    <p>本文エリアでは太字、見出し、文字色、リンク、中央寄せ、箇条書きが使えます。</p>
                    <p>画像は本文へドラッグ&ドロップ、または「画像追加」で本文に仮配置され、下書き保存または投稿申請のときに Cloudinary へ保存されます。</p>
                    <p>冒頭画像は本文に入っている画像の中から、本文エディタの下で選びます。</p>
                  </div>
                  {postEditorMode === 'edit' ? (
                    <label>編集する記事
                      <select value={targetPostId} onChange={(e) => setTargetPostId(e.target.value)} required>
                        <option value="">選択してください</option>
                        {postOptions.map((row) => (
                          <option key={row.id} value={row.id}>{row.title || row.id}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <label>記事タイトル<input value={post.title} onChange={(e) => updatePostField('title', e.target.value)} required /></label>
                  <label>日付<input type="date" value={post.publishedDate} onChange={(e) => updatePostField('publishedDate', e.target.value)} /></label>
                  <label>リード文（記事上部の要約）<textarea value={post.body} onChange={(e) => updatePostField('body', e.target.value)} rows={5} required /></label>
                  <label>記事タグ(カンマ区切り)<input value={post.tags} onChange={(e) => updatePostField('tags', e.target.value)} placeholder="マルシェ, お知らせ" /></label>
                  <label>野菜タグ(カンマ区切り)<input value={post.cropTags} onChange={(e) => updatePostField('cropTags', e.target.value)} placeholder="トマト, 米" /></label>
                  <label>対象農家（複数追加）
                    <select value={postFarmerCandidateId} onChange={(e) => setPostFarmerCandidateId(e.target.value)}>
                      <option value="">対象なし / 選択しない</option>
                      {postFarmerOptions.map((row) => (
                        <option key={row.id} value={row.id}>{row.name} / {row.prefecture} {row.city}</option>
                      ))}
                    </select>
                  </label>
                  <div className="row-actions">
                    <button type="button" className="btn btn-secondary" onClick={handleAddPostFarmerTag} disabled={!postFarmerCandidateId}>対象農家を追加</button>
                  </div>
                  {postFarmerTagIds.length > 0 ? (
                    <div className="tag-row">
                      {postFarmerTagIds.map((farmerId) => {
                        const matched = postFarmerOptions.find((row) => row.id === farmerId);
                        const label = matched ? `${matched.name}` : farmerId;
                        return (
                          <button key={farmerId} type="button" className="pill" onClick={() => handleRemovePostFarmerTag(farmerId)}>
                            {label} ×
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="hint">対象農家は未選択でも保存できます。</p>
                  )}
                  <label>対象ドクター（複数追加）
                    <select value={postDoctorCandidateId} onChange={(e) => setPostDoctorCandidateId(e.target.value)}>
                    <option value="">対象なし / 選択しない</option>
                    {doctorTagOptions.map((row) => (
                      <option key={row.slug || row.id} value={row.slug || row.id}>{formatDoctorName(row.name || row.id)}</option>
                    ))}
                  </select>
                </label>
                  <div className="row-actions">
                    <button type="button" className="btn btn-secondary" onClick={handleAddPostDoctorTag} disabled={!postDoctorCandidateId}>対象ドクターを追加</button>
                  </div>
                  {postDoctorTagIds.length > 0 ? (
                    <div className="tag-row">
                    {postDoctorTagIds.map((doctorId) => {
                      const matched = doctorTagOptions.find((row) => (row.slug || row.id) === doctorId);
                      const label = matched ? formatDoctorName(matched.name) : doctorId;
                      return (
                        <button key={doctorId} type="button" className="pill" onClick={() => handleRemovePostDoctorTag(doctorId)}>
                          {label} ×
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="hint">対象ドクターが未選択です。</p>
                  )}
                  <label>YouTube URL<input value={post.youtubeUrl} onChange={(e) => updatePostField('youtubeUrl', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." /></label>
                  <div className="editor-blocks">
                    <strong>本文エディタ</strong>
                    <RichArticleEditor
                      ref={postEditorRef}
                      value={post.contentHtml}
                      onChange={(html) => updatePostField('contentHtml', html)}
                      user={user}
                    />
                  </div>
                  {postIntroImageOptions.length > 0 ? (
                    <label>冒頭画像
                      <select
                        value={post.introImageUrl}
                        onChange={(e) => updatePostField('introImageUrl', e.target.value)}
                      >
                        {postIntroImageOptions.map((imageUrl, index) => (
                          <option key={imageUrl || 'none'} value={imageUrl}>{imageUrl ? `本文画像 ${index}` : 'なし'}</option>
                        ))}
                      </select>
                    </label>
                  ) : (
                    <p className="hint post-editor-note">本文画像がまだないため、冒頭画像の選択は表示していません。</p>
                  )}
                  <div className="row-actions">
                    <button type="button" className={`btn ${postPreviewDevice === 'pc' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleTogglePostPreview('pc')}>
                      PCプレビュー
                    </button>
                    <button type="button" className={`btn ${postPreviewDevice === 'mobile' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleTogglePostPreview('mobile')}>
                      スマホプレビュー
                    </button>
                  </div>
                  <p className="hint post-editor-note">本文や記事情報を変更したあとは、PC版とスマホ版の両方を確認すると申請できます。</p>
                  {postPreviewDevice ? (
                    <div className={`article-preview-card article-preview-card-${postPreviewDevice}`}>
                      <p className="hint">{postPreviewDevice === 'mobile' ? 'スマホ版プレビュー' : 'PC版プレビュー'}</p>
                      <article className="article article-preview-sheet article-rich-content">
                        <h1>{post.title || '記事タイトル'}</h1>
                        {post.publishedDate ? <p className="meta-text">{post.publishedDate}</p> : null}
                        {post.tags ? <p className="meta-text">タグ: {post.tags}</p> : null}
                        {post.cropTags ? <p className="meta-text">野菜タグ: {post.cropTags}</p> : null}
                        {post.youtubeUrl ? <p className="meta-text">YouTube: {post.youtubeUrl}</p> : null}
                        <p>{post.body || 'リード文がここに表示されます。'}</p>
                        <div className="article article-rich-content" dangerouslySetInnerHTML={{ __html: postPreviewHtml || '<p></p>' }} />
                      </article>
                    </div>
                  ) : null}
                  <div className="row-actions">
                    {postEditorMode === 'create' ? (
                      <>
                        <button className="btn btn-outline-solid" type="submit">下書き保存</button>
                        <div className="action-feedback-stack">
                          <button className="btn btn-secondary" type="button" onClick={handleCreatePostAndSubmitReview}>投稿申請</button>
                          {postSubmitStatus ? (
                            <p className={postSubmitStatus.type === 'success' ? 'success-text' : 'error-text'}>
                              {postSubmitStatus.text}
                            </p>
                          ) : null}
                          {!canSubmitPost ? <p className="hint action-feedback-hint">PC版とスマホ版の両方を確認すると申請できます。</p> : null}
                          {showSubmitDebug && postSubmitDebug ? (
                            <pre className="action-debug-panel">{JSON.stringify(postSubmitDebug, null, 2)}</pre>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-outline-solid" type="submit" disabled={!targetPostId}>下書きを更新</button>
                        <div className="action-feedback-stack">
                          <button className="btn btn-secondary" type="button" onClick={handleSubmitPostReview} disabled={!targetPostId}>投稿申請</button>
                          {postSubmitStatus ? (
                            <p className={postSubmitStatus.type === 'success' ? 'success-text' : 'error-text'}>
                              {postSubmitStatus.text}
                            </p>
                          ) : null}
                          {targetPostId && !canSubmitPost ? <p className="hint action-feedback-hint">変更後はPC版とスマホ版の両方を確認すると申請できます。</p> : null}
                          {showSubmitDebug && postSubmitDebug ? (
                            <pre className="action-debug-panel">{JSON.stringify(postSubmitDebug, null, 2)}</pre>
                          ) : null}
                        </div>
                        <button className="btn btn-danger" type="button" onClick={async () => {
                          if (!window.confirm('運営記事を削除しますか？この操作は元に戻せません。')) return;
                          setPostSubmitStatus(null);
                          try {
                            await deletePost(targetPostId);
                            setTargetPostId('');
                            setPost(createEmptyPost());
                            setPostFarmerTagIds([]);
                            setPostDoctorTagIds([]);
                            setPostOptions(await listPostsForAdmin());
                            setPostSubmitStatus({ type: 'success', text: '運営記事を削除しました。' });
                          } catch (error) {
                            setPostSubmitStatus({ type: 'error', text: `運営記事の削除に失敗しました。${error?.message ? ` ${error.message}` : ''}` });
                          }
                        }} disabled={!targetPostId}>削除</button>
                      </>
                    )}
                  </div>
                  <p className="hint">投稿の流れ: プレビュー確認 → 投稿申請 → 運営承認 → 公開です。</p>
                </form>
              </div>
            )}
          </article>
        </section>
      ) : null}
    </>
  );
}
